<template>
    <base-sheet
        id="flash-send-sidebar"
        width="lg"
        static-backdrop
        :title="displayMessageDetail ? $t('detail-flash-message-sent') : $t('create-flash-send')"
        :subtitle="displayMessageDetail ? '' : $t('send-multi-sms')"
        :hide-cancel="displayMessageDetail"
        :ok-text="okText"
        :ok-disabled="!displayMessageDetail && (isFormDisabled || disabledSubmit)"
        :busy="loading"
        @ok="submit()"
        @hidden="clearFlashSend()"
    >
        <form>
            <div class="form-group text-left">
                <label for="recipient" class="col-form-label fs-14">
                    {{ $tc('recipient', displayMessageDetail ? audienceCount : to.length) }}
                    <strong class="obligatory" v-if="!displayMessageDetail">*</strong>
                    <span class="font-italic text-dark-brown font-nunito fs-13 ml-2" v-if="displayMessageDetail">({{ audienceCount }} contacts)</span>
                </label>
                    <template v-if="displayMessageDetail">
                        <div class="base-chips-input" v-if="hasAudiences">
                            <div class="base-chips-input-list">
                                <span v-for="phoneNumber in selectedFlashMessage.audiences" :key="phoneNumber" class="base-chips-input-chip is-flash" :title="phoneNumber">{{ recipientLabel(selectedFlashMessage, phoneNumber) }}</span>
                            </div>
                        </div>
                        <p class="fs-13" v-else>{{ $t('all') }}</p>
                    </template>
                    <search-input v-else :defaultList="defaultList" v-on:listUpdated="updateContactList($event)"></search-input>
                    <div class="invalid-feedback" v-if="submitted  && errors.has('recipient')">
                        {{ errors.first('recipient') }}
                    </div>
                    <div class="text-left mt-2" v-if="displayMessageDetail">
                        <span class="font-italic text-grey-9 font-nunito fs-13">
                            ({{ selectedFlashMessage.sentCount ? selectedFlashMessage.sentCount : 0 }} {{ $tc('sent', selectedFlashMessage.sentCount) }},
                            {{ selectedFlashMessage.failedCount ? selectedFlashMessage.failedCount : 0 }} {{ $tc('failed', selectedFlashMessage.failedCount) }})
                        </span>
                    </div>
            </div>
            <div class="form-group text-left" v-if="!displayMessageDetail">
                <label for="template" class="col-form-label fs-14">{{ $t('template') }}</label>
                <select-dropdown
                    :options="options"
                    :selected="selectedTemplate"
                    v-on:selectTemplate="chooseTemplate($event)"
                    :showLabel="false"
                    :type="'template'"
                    :isDisabled="isFormDisabled"
                >
                </select-dropdown>
                <div class="invalid-feedback" v-if="submitted && errors.has('template')">
                    {{ errors.first('template') }}
                </div>
            </div>
            <div class="form-group text-left">
                <label for="message" class="col-form-label fs-14">
                    {{ $t('message') }}
                    <strong class="obligatory" v-if="!displayMessageDetail">*</strong>
                </label>
                <div v-if="displayMessageDetail">
                    <p class="fs-13">{{ message }}</p>
                </div>
                <div v-else>
                    <textarea
                        id="message"
                        name="message"
                        type="text"
                        class="form-control fs-13"
                        rows="6"
                        :placeholder="isFormDisabled ? chatPlaceholder : $t('template-text-placeholder')"
                        v-model="message"
                        v-validate="'required'"
                        :class="{ 'is-invalid': submitted && errors.has('message'), 'disabled-textarea': isFormDisabled}"
                        :disabled="isFormDisabled"
                    ></textarea>
                    <sms-counter :sms="message" v-on:limitExceeded="smsLimitExceeded = $event"></sms-counter>
                    <div class="invalid-feedback" v-if="submitted && errors.has('message')">
                        {{ errors.first('message') }}
                    </div>
                </div>
            </div>
            <div class="name-content d-flex flex-column mb-5" v-if="displayMessageDetail">
                <h6 class="font-nunito fs-14">{{ $t('time-date-to-send') }}</h6>
                <div class="d-flex d-inline-flex">
                    <span class="text-dark-brown text-left mr-5 font-nunito fs-13">
                        {{ fromUtc(selectedFlashMessage.sentAt).date }} {{ fromUtc(selectedFlashMessage.sentAt).time }} <timezone></timezone>
                    </span>
                </div>
            </div>
            <div class="mb-4 send-time" v-else>
                <router-link :to="{ name: '' }" type="button" class="text-btn style-btn fs-14 px-0" @click.native="showTimeSection()">
                    {{  $t('plan-flash-message') }}
                    <font-awesome-icon :icon="['fas', 'angle-up']" v-if="shownSendTime"/>
                    <font-awesome-icon :icon="['fas', 'angle-down']" v-else/>
                </router-link>
                <div class="date-time-to-send-section form-group mb-5" v-if="shownSendTime">
                    <div class="d-flex flex-column">
                        <label class="font-nunito fs-14 text-dark mb-0" for="date-time">
                            {{ $t('time-date-to-send') }}
                        </label>
                        <small class="text-left text-black font-nunito">
                            {{ $t('schedule-time-to-send') }}
                        </small>
                        <small class="btn-link text-decoration">
                            <timezone>{{ $t('learn-about-utc') }}</timezone>
                        </small>
                    </div>
                    <div class="row mt-3">
                        <div class="col">
                            <label for="planned-date" class="font-nunito fs-12 text-darker-grey mb-0">
                                {{ $t('Date') }}
                            </label>
                            <input type="date" class="form-border"
                                name="planned-date"
                                v-validate="`date_format:yyyy-MM-dd|after:${minDate}${dateRules}`"
                                v-model="flashMessage['planned-date']"
                            >
                            <div class="invalid-feedback" v-if="submitted && errors.has('planned-date')">
                                {{ errors.first('planned-date') }}
                            </div>
                        </div>
                        <div class="col">
                            <label for="planned-time" class="font-nunito fs-12 text-darker-grey mb-0">
                                {{ $t('utc-time') }}
                            </label>
                            <input type="time" class="form-border"
                                name="planned-time"
                                v-validate="timeRules"
                                v-model="flashMessage['planned-time']"
                            >
                            <div class="invalid-feedback" v-if="submitted && errors.has('planned-time')">
                                {{ errors.first('planned-time') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </base-sheet>
</template>
<script>
import Vue from 'vue';

// Components
import SelectDropdown from '@/core/components/SelectDropdown.vue';
import SearchInput    from '@/connectors/sms/components/SearchInput.vue';
import SmsCounter     from '@/connectors/sms/components/SmsCounter.vue';
import Timezone       from '@/core/components/Timezone.vue';
import BaseSheet      from '@/core/components/BaseSheet.vue';

// Internal services js
import {
    mapActions,
    mapState,
    mapGetters
} from 'vuex';
import Alert        from '@/helpers/Alert.js';
import { EventBus } from '@/helpers/EventBus.js';
import moment       from 'moment';
import Format       from '@/helpers/Format.js';

import ReplaceMessageMixin from '@/core/mixins/ReplaceMessageMixin';
import RecipientNamesMixin from '@/core/mixins/RecipientNamesMixin';

// Import Really Fast Deep Copy
const clone = require('rfdc')();

export default {
    mixins: [ReplaceMessageMixin, RecipientNamesMixin],

    props: ['editMessage', 'displayMessageDetail'],

    components: {
        BaseSheet,
        Timezone,
        SelectDropdown,
        SearchInput,
        SmsCounter
    },

    data () {
        const FLASH_CATEGORY = 'flash';

        return {
            // Template
            selectedTemplate: this.$i18n.t('choose-template'),
            options:          [],
            smsLimitExceeded: false,
            shownSendTime:    false,
            defaultList:      [],

            // Scheduled Flash details
            to:           [],
            message:      '',
            flashMessage: {
                name:           'flash name',
                category:       FLASH_CATEGORY,
                content:        '',
                audiences:      [],
                'planned-date': '',
                'planned-time': ''
            },

            submitted: false,
            loading:   false
        };
    },

    computed: {
        ...mapState('core/scheduled-messages', {
            flashMessages:        'plannedScheduledMessages',
            selectedFlashMessage: 'selectedScheduledMessage'
        }),

        okText () {
            if (this.displayMessageDetail) {
                return this.$t('close');
            }

            return this.shownSendTime ? this.$t('send-at-scheduled-time') : this.$t('send-now');
        },

        /**
         * Whether the selected flash message targets specific recipients
         *
         * @return {boolean}
         */
        hasAudiences () {
            return Array.isArray(this.selectedFlashMessage.audiences) && this.selectedFlashMessage.audiences.length > 0;
        },

        /**
         * Number of contacts targeted by the selected flash message
         *
         * @return {number}
         */
        audienceCount () {
            return this.selectedFlashMessage.audienceCount || 0;
        },

        ...mapState('core/templates', [
            'templates'
        ]),

        // Getter for the selected phone number
        ...mapGetters('core/number', { activePhoneNumber: 'smsPhoneNumber', numberToActivate: 'numberToActivate' }),

        isFormDisabled () {
            if (!this.activePhoneNumber || this.numberToActivate) {
                return true;
            }

            return false;
        },

        /**
         * Get the Chat placeholder
         *
         * @return {string}
         */
        chatPlaceholder () {
            if (this.numberToActivate) {
                // Phone waiting to be approved
                return this.$i18n.t('phone-not-activated-yet');
            }

            // No active phone number
            return this.$i18n.t('no-phone-number-configured');
        },

        disabledSubmit () {
            if (this.to.length !== 0 && this.message && !this.smsLimitExceeded) {
                return false;
            }

            return true;
        },

        /**
         * Get the min date (yesterday)
         */
        minDate () {
            return moment().subtract(1, 'days')
                .format('yyyy-MM-DD')
                .toString();
        },

        /**
         * Validates the planned date
         *
         * @return boolean
         */
        dateRules () {
            return this.flashMessage['planned-time'] ? '|required' : '';
        },

        /**
         * Validates the planned time
         *
         * @return boolean
         */
        timeRules () {
            return this.flashMessage['planned-date'] ? '|required' : '';
        }
    },

    mounted () {
        this.getTemplates();
    },

    watch: {
        /**
         * Selected flash message watcher
         *
         * @param   {Object}  value   The value
         */
        selectedFlashMessage (value) {
            this.flashMessage.audiences = clone(value.audiences);
            this.flashMessage.content = clone(value.content);

            if (value.plannedDate !== '' && value.plannedTime !== '') {
                const date = Format.fromUtc(`${value.plannedDate} ${value.plannedTime}`);
                this.flashMessage['planned-date'] = date.date;
                this.flashMessage['planned-time'] = date.time;
            }

            this.message     = clone(value.content);
            this.defaultList = clone(value.audiences);

            if (this.flashMessage['planned-date'] && this.flashMessage['planned-time']) {
                this.shownSendTime = true;
            } else {
                this.shownSendTime = false;
            }
        }
    },

    methods: {
        /**
         * Load templates
         */
        ...mapActions('core/templates', { getTemplate: 'index' }),

        ...mapActions('core/scheduled-messages', {
            create:                    'create',
            update:                    'update',
            send:                      'send',
            resetSelectedFlashMessage: 'resetSelectedScheduledMessage'
        }),

        ...mapActions('core/contacts', { getContacts: 'index' }),

        /**
         * Helper method to format date to use in template
         */
        fromUtc: Format.fromUtc,

        /**
         * Get the list of available templates
         */
        getTemplates () {
            this.getTemplate().then(() => {
                this.options.push({
                    'id':      null,
                    'name':    this.$i18n.t('choose-template'),
                    'content': ''
                });
                for (const templateId in this.templates) {
                    this.options.push({
                        'id':      templateId,
                        'name':    this.templates[templateId].attributes.name,
                        'content': this.templates[templateId].attributes.content
                    });
                }
            });
        },

        /**
         * Update selected option
         *
         * @return    void
         */
        chooseTemplate (option) {
            if (option.id === null) {
                this.selectedTemplate    = option.name;
                this.disabled            = option.id === null;
            } else if (this.message === '') {
                this.selectedTemplate = option.name;
                this.message          = option.content;
            } else {
                this.confirmReplaceMessage()
                    .then((confirmed) => {
                        if (confirmed) {
                            this.selectedTemplate = option.name;
                            this.message = option.content;
                        }
                    });
            }
        },

        /**
         * When the liste of contacts is updated, update the TO list
         *
         * @param {object}  event   the event
         */
        updateContactList (event) {
            this.to = event;
        },

        /**
         * Create a flash scheduled message to one or multiple contacts
         */
        createFlash () {
            if (this.isFormDisabled || this.disabledSubmit) {
                return;
            }

            Vue.prototype.$validation = this.$validator;

            this.loading   = true;
            this.submitted = true;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    this.loading = false;
                    return;
                }

                // Set the new flash content and audiences
                this.flashMessage.content   = this.message;
                this.flashMessage.audiences = [];
                this.to.forEach(element => {
                    this.flashMessage.audiences.push(element.phoneNumber);
                });

                // Format date to UTC
                const date = this.flashMessage['planned-date'] && this.flashMessage['planned-time'] ?
                    Format.toUtc(`${this.flashMessage['planned-date']} ${this.flashMessage['planned-time']}`) : null;
                const attributes = clone(this.flashMessage);
                attributes['planned-date'] = date ? date.date : null;
                attributes['planned-time'] = date ? date.time : null;

                this.create({ attributes })
                    .then((response) => {
                        if (this.shownSendTime === false) {
                            this.sendFlash(response.data.data.id);
                        } else {
                            this.loading   = false;
                            this.submitted = false;
                            this.$emit('flashListUpdated');
                            Alert.success(this.$i18n.t('flash-send-created'));
                            this.clearFlashSend();
                        }
                    })
                    .catch(() => {
                        this.loading   = false;
                        this.submitted = false;
                    });
            });
        },

        /**
         * Edit the selected flash message
         */
        editFlash () {
            if (this.isFormDisabled || this.disabledSubmit) {
                return;
            }

            Vue.prototype.$validation = this.$validator;

            this.loading   = true;
            this.submitted = true;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    this.loading = false;
                    return;
                }

                // Set the updated flash content and audiences
                this.flashMessage.content   = this.message;
                this.flashMessage.audiences = [];
                this.to.forEach(element => {
                    this.flashMessage.audiences.push(element.phoneNumber);
                });

                // Format date to UTC
                const date = this.flashMessage['planned-date'] && this.flashMessage['planned-time'] ?
                    Format.toUtc(`${this.flashMessage['planned-date']} ${this.flashMessage['planned-time']}`) : null;
                const attributes = clone(this.flashMessage);
                attributes['planned-date'] = date ? date.date : null;
                attributes['planned-time'] = date ? date.time : null;

                this.update({
                    id: this.selectedFlashMessage.id,
                    attributes
                })
                    .then(() => {
                        if (this.shownSendTime === false) {
                            this.sendFlash(this.selectedFlashMessage.id);
                        } else {
                            this.loading   = false;
                            this.submitted = false;
                            this.$emit('flashListUpdated');
                            Alert.success(this.$i18n.t('flash-send-updated'));
                            this.clearFlashSend();
                        }
                    })
                    .catch(() => {
                        this.loading   = false;
                        this.submitted = false;
                    });
            });
        },

        /**
         * Send the scheduled message now
         */
        sendFlash (id) {
            this.$dialog.confirm({
                title:       this.$t('title-flash-message-send'),
                text:        this.$t('confirm-scheduled-send'),
                centerText:  true,
                image:       '/images/confirm-send-scheduled-message.png',
                confirmText: this.$t('send-scheduled-message')
            })
                .then((confirmed) => {
                    if (!confirmed) {
                        this.loading = false;
                        this.$emit('flashListUpdated');
                        this.clearFlashSend();
                        return;
                    }

                    this.send({ id })
                        .then(() => {
                            this.loading   = false;
                            this.submitted = false;
                            this.$emit('flashListUpdated');
                            Alert.success(this.$i18n.t('scheduled-send-success'));
                            this.clearFlashSend();
                        })
                        .catch(() => {
                            this.loading = false;
                        });
                });
        },

        /**
         * Show the date and time section
         */
        showTimeSection () {
            this.shownSendTime = !this.shownSendTime;

            if (!this.shownSendTime) {
                this.flashMessage['planned-date'] = '';
                this.flashMessage['planned-time'] = '';
            }
        },

        /**
         * Clear all form inputs
         */
        submit () {
            if (this.displayMessageDetail) {
                this.$bvModal.hide('flash-send-sidebar');
                return;
            }

            if (this.editMessage) {
                this.editFlash();
            } else {
                this.createFlash();
            }
        },

        clearFlashSend () {
            this.to               = [];
            this.message          = '';
            this.selectedTemplate = this.$i18n.t('choose-template');
            EventBus.$emit('clear-contact-list');
            this.resetSelectedFlashMessage();
            this.$bvModal.hide('flash-send-sidebar');
        }
    }
};
</script>
